// Client-side composite recorder (F-REC-1, §10). Because the meeting is E2EE,
// server-side recording is impossible by design — we capture "what the
// recorder saw" locally: a composite canvas of the video tiles + a Web Audio
// mix of every decrypted track (remote + self), fed to MediaRecorder as
// chunked VP9/Opus webm. Chunks are surfaced every 10s so a crash loses ≤10s.
//
// The composited frame source (which tiles, where) is provided by the caller
// via a draw callback, so the engine stays framework-free — the UI knows the
// layout; this owns the capture pipeline.

export interface RecorderTargets {
  /** Draw the current composite frame; called ~30fps. */
  draw: (ctx: CanvasRenderingContext2D, width: number, height: number) => void;
  /** Remote + local audio MediaStreamTracks to mix into the recording. */
  audioTracks: () => MediaStreamTrack[];
}

export interface RecordingResult {
  blob: Blob;
  mimeType: string;
  durationMs: number;
}

const CHUNK_MS = 10_000; // ≤10s lost on crash (§10.3)

export class CompositeRecorder {
  private canvas: HTMLCanvasElement;
  private raf = 0;
  private recorder: MediaRecorder | null = null;
  private chunks: Blob[] = [];
  private audioCtx: AudioContext | null = null;
  private startedAt = 0;

  constructor(private targets: RecorderTargets, width = 1280, height = 720) {
    this.canvas = document.createElement('canvas');
    this.canvas.width = width;
    this.canvas.height = height;
  }

  get active(): boolean {
    return this.recorder?.state === 'recording';
  }

  /** Begins capture; resolves once MediaRecorder is running. */
  async start(onChunk?: (chunk: Blob) => void): Promise<void> {
    const ctx = this.canvas.getContext('2d');
    if (!ctx) throw new Error('canvas 2d unavailable');

    const drawLoop = () => {
      this.targets.draw(ctx, this.canvas.width, this.canvas.height);
      this.raf = requestAnimationFrame(drawLoop);
    };
    this.raf = requestAnimationFrame(drawLoop);

    const videoStream = this.canvas.captureStream(30);

    // Mix all provided audio tracks into one destination.
    this.audioCtx = new AudioContext();
    const dest = this.audioCtx.createMediaStreamDestination();
    for (const track of this.targets.audioTracks()) {
      const src = this.audioCtx.createMediaStreamSource(new MediaStream([track]));
      src.connect(dest);
    }
    for (const t of dest.stream.getAudioTracks()) videoStream.addTrack(t);

    const mimeType = pickMime();
    this.recorder = new MediaRecorder(videoStream, { mimeType, videoBitsPerSecond: 3_000_000 });
    this.chunks = [];
    this.recorder.ondataavailable = (e) => {
      if (e.data.size > 0) {
        this.chunks.push(e.data);
        onChunk?.(e.data);
      }
    };
    this.startedAt = performance.now();
    this.recorder.start(CHUNK_MS);
  }

  /** Stops and returns the finalized recording. */
  async stop(): Promise<RecordingResult> {
    const recorder = this.recorder;
    if (!recorder) throw new Error('not recording');
    const mimeType = recorder.mimeType;
    const done = new Promise<void>((resolve) => {
      recorder.onstop = () => resolve();
    });
    recorder.stop();
    await done;
    cancelAnimationFrame(this.raf);
    await this.audioCtx?.close();
    this.audioCtx = null;
    this.recorder = null;
    return {
      blob: new Blob(this.chunks, { type: mimeType }),
      mimeType,
      durationMs: performance.now() - this.startedAt,
    };
  }
}

/** Recording capability depends on MediaRecorder + a supported webm codec. */
export function recordingSupported(): boolean {
  return (
    typeof MediaRecorder !== 'undefined' &&
    typeof HTMLCanvasElement.prototype.captureStream === 'function' &&
    (MediaRecorder.isTypeSupported('video/webm;codecs=vp9,opus') ||
      MediaRecorder.isTypeSupported('video/webm;codecs=vp8,opus'))
  );
}

function pickMime(): string {
  const prefs = [
    'video/webm;codecs=vp9,opus',
    'video/webm;codecs=vp8,opus',
    'video/webm',
  ];
  return prefs.find((m) => MediaRecorder.isTypeSupported(m)) ?? 'video/webm';
}
